import { apiUrl } from "../utils/api";

/** base64url (VAPID-Public-Key) → Uint8Array für pushManager.subscribe. */
// Rueckgabetyp bewusst als `Uint8Array<ArrayBuffer>`: das blosse `Uint8Array` ist
// seit TS 5.7 `Uint8Array<ArrayBufferLike>` und damit keine `BufferSource` mehr,
// die `PushManager.subscribe({ applicationServerKey })` akzeptiert.
const urlBase64ToUint8Array = (base64String: string): Uint8Array<ArrayBuffer> => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

const normalizeBase64Url = (value: string): string => value.replace(/=+$/g, "").replace(/\+/g, "-").replace(/\//g, "_");

const arrayBufferToBase64Url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return normalizeBase64Url(btoa(binary));
};

export const subscriptionUsesVapidKey = (sub: Pick<PushSubscription, "options">, vapidPublicKey: string): boolean => {
  const currentKey = sub.options?.applicationServerKey;
  if (!currentKey) return true;
  return arrayBufferToBase64Url(currentKey) === normalizeBase64Url(vapidPublicKey);
};

/** Warum ein Abo (nicht) zustande kam. Die Oberfläche zeigt daraus den passenden
 *  Hinweis; ohne diesen Rückgabewert endete jeder Fehlschlag stumm in der Konsole. */
export type AboErgebnis = {
  ok: boolean;
  grund?:
    | "nicht-unterstuetzt"
    | "blockiert"
    | "nicht-gefragt"
    | "nicht-erlaubt"
    | "server-aus"
    | "abo-fehler"
    | "backend";
  /** Klartext-Meldung des Browsers oder des Backends. */
  fehler?: string;
};

// In-Flight-Dedup für ensureSubscribed: gleichzeitige Aufrufe (AuthContext +
// NavigationLayout beim Start) teilen sich EINE Ausführung → nur 1× vapid + 1× subscribe.
let ensureSubscribedInFlight: Promise<AboErgebnis> | null = null;

export const pushService = {
  supported(): boolean {
    return typeof navigator !== "undefined" && "serviceWorker" in navigator && typeof window !== "undefined" && "PushManager" in window;
  },

  async vapidPublicKey(): Promise<string | null> {
    try {
      const res = await fetch(apiUrl("push/vapid-public-key"));
      if (!res.ok) {
        console.warn(`[PushService] VAPID-Public-Key-Abfrage fehlgeschlagen (HTTP-Status ${res.status})`);
        return null;
      }
      const data = (await res.json()) as { publicKey: string | null };
      return data.publicKey ?? null;
    } catch (err) {
      console.error("[PushService] VAPID-Public-Key-Abfrage fehlgeschlagen:", err);
      return null;
    }
  },

  /** Stellt sicher, dass ein Push-Abo besteht. Fragt – falls nötig und
   *  `interactive` – die Erlaubnis an (muss aus einer Nutzer-Geste kommen).
   *  No-op, wenn nicht unterstützt, abgelehnt oder Push serverseitig deaktiviert.
   *  Parallele Aufrufe teilen sich EINE Ausführung (Dedup). */
  async ensureSubscribed(opts?: { interactive?: boolean }): Promise<AboErgebnis> {
    if (ensureSubscribedInFlight) return ensureSubscribedInFlight;
    ensureSubscribedInFlight = this._ensureSubscribedImpl(opts).finally(() => {
      ensureSubscribedInFlight = null;
    });
    return ensureSubscribedInFlight;
  },

  async _ensureSubscribedImpl(opts?: { interactive?: boolean }): Promise<AboErgebnis> {
    if (!this.supported()) {
      console.warn("[PushService] Web-Push nicht unterstützt (kein Service Worker oder PushManager im Browser vorhanden)");
      return { ok: false, grund: "nicht-unterstuetzt" };
    }
    if (Notification.permission === "denied") {
      console.warn("[PushService] Benachrichtigungserlaubnis im Browser verweigert (denied)");
      return { ok: false, grund: "blockiert" };
    }
    if (Notification.permission === "default") {
      if (!opts?.interactive) return { ok: false, grund: "nicht-gefragt" }; // ohne Geste nicht fragen
      console.log("[PushService] Notification.permission ist 'default', frage Berechtigung an...");
      const perm = await Notification.requestPermission();
      console.log(`[PushService] Berechtigungsergebnis: ${perm}`);
      if (perm !== "granted") return { ok: false, grund: "nicht-erlaubt" };
    }
    try {
      console.log("[PushService] Warte auf Service Worker...");
      const reg = await navigator.serviceWorker.ready;
      const key = await this.vapidPublicKey();
      if (!key) {
        console.warn("[PushService] Abonnement abgebrochen: VAPID Public Key ist leer oder deaktiviert");
        return { ok: false, grund: "server-aus" };
      }
      let sub = await reg.pushManager.getSubscription();
      if (sub && !subscriptionUsesVapidKey(sub, key)) {
        console.log("[PushService] Bestehendes Push-Abonnement nutzt einen alten VAPID Key, erneuere Abo...");
        await sub.unsubscribe();
        sub = null;
      }
      if (!sub) {
        console.log("[PushService] Kein aktuelles Push-Abonnement gefunden, erstelle Push-Abonnement im Browser...");
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(key),
        });
        console.log("[PushService] Push-Abonnement im Browser erfolgreich erstellt:", sub);
      }
      console.log("[PushService] Sende Push-Abonnement an das Backend...", sub);
      const res = await fetch(apiUrl("push/subscribe"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sub),
      });
      if (res.ok) {
        console.log("[PushService] Abonnement erfolgreich im Backend registriert");
        return { ok: true };
      }
      console.error(`[PushService] Registrierung im Backend fehlgeschlagen (HTTP-Status ${res.status})`);
      return { ok: false, grund: "backend", fehler: `Das Backend antwortete mit HTTP ${res.status}.` };
    } catch (err) {
      console.error("[PushService] Push-Abonnement-Fehler (Web-Push):", err);
      /* Subscribe fehlgeschlagen — In-App-Toasts funktionieren weiterhin. */
      return { ok: false, grund: "abo-fehler", fehler: String((err as Error)?.message || err) };
    }
  },
};
